import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect } from "react";

import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { PortalSidebar } from "@/components/portal-sidebar";
import { AdminSidebar } from "@/components/admin-sidebar";
import { ProfileMenu } from "@/components/profile-menu";
import { ImpersonationBanner } from "@/components/impersonation-banner";
import { Toaster } from "@/components/ui/sonner";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const router = useRouter();

  // Bare layout: login pages
  const bare =
    pathname === "/auth" ||
    pathname.startsWith("/portal/login") ||
    pathname.startsWith("/admin/login");

  // Global mock-auth gate (client-only). Redirects unauthenticated users and
  // wrong-role users to the appropriate login. Runs after hydration so SSR
  // markup isn't affected.
  useEffect(() => {
    if (bare) return;
    if (typeof window === "undefined") return;
    const raw = window.localStorage.getItem("mockSession");
    const session = raw ? (JSON.parse(raw) as { role: string }) : null;
    const isPortalPath = pathname.startsWith("/portal");
    const isAdminPath = pathname.startsWith("/admin");
    if (!session) {
      const to = isAdminPath ? "/admin/login" : isPortalPath ? "/portal/login" : "/auth";
      router.navigate({ to, replace: true });
      return;
    }
    if (isAdminPath && session.role !== "superadmin") {
      router.navigate({ to: "/auth", replace: true });
      return;
    }
    if (isPortalPath && session.role !== "client_portal") {
      router.navigate({ to: "/auth", replace: true });
      return;
    }
    if (!isAdminPath && !isPortalPath) {
      // studio area
      if (session.role === "superadmin") {
        router.navigate({ to: "/admin", replace: true });
        return;
      }
      if (session.role === "client_portal") {
        router.navigate({ to: "/portal", replace: true });
        return;
      }
    }
  }, [pathname, bare, router]);

  if (bare) {
    return (
      <QueryClientProvider client={queryClient}>
        <Outlet />
        <Toaster />
      </QueryClientProvider>
    );
  }

  const isPortal = pathname.startsWith("/portal");
  const isAdmin = pathname.startsWith("/admin");
  const Sidebar = isAdmin ? AdminSidebar : isPortal ? PortalSidebar : AppSidebar;
  const label = isAdmin ? "Platform Admin" : isPortal ? "Client Portal" : "Studio OS";

  return (
    <QueryClientProvider client={queryClient}>
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <Sidebar />
          <div className="flex min-w-0 flex-1 flex-col">
            <ImpersonationBanner />
            <header className="sticky top-0 z-10 flex h-12 items-center gap-2 border-b border-border bg-background/80 px-3 backdrop-blur">
              <SidebarTrigger />
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                {label}
              </span>
              <div className="ml-auto">
                <ProfileMenu />
              </div>
            </header>
            <main className="flex-1 overflow-x-hidden">
              <Outlet />
            </main>
          </div>
        </div>
        <Toaster />
      </SidebarProvider>
    </QueryClientProvider>
  );
}
